export const en = {
  nav: {
    overview: 'Overview', creators: 'Creators', followups: 'Follow-ups', approvals: 'Approvals',
    partnerships: 'Partnerships & Contracts', contracts: 'Contracts', agents: 'AI Agents', activity: 'Activity',
    connectors: 'Connectors', system: 'System', settings: 'Settings',
    content: 'Content', crm: 'Leads & CRM', social: 'Social', ads: 'Ads',
    clients: 'Clients', projects: 'Projects', finance: 'Finance',
    aiCommand: 'AI Command', aiOperations: 'AI Operations',
    aiModels: 'AI Models', automations: 'Automations', memory: 'Shared Memory', agentLog: 'Agent Log', support: 'Support & Diagnostics',
  },
  navGroups: {
    command: 'Command', operations: 'Operations', growth: 'Growth',
    business: 'Business', ai: 'AI & Automation', system: 'System',
  },
  common: {
    comingSoon: 'Soon', viewAll: 'View all', close: 'Close', live: 'Live', comingNext: 'Coming next',
    notConnected: 'Not connected', requiresBackend: 'Requires backend', awaitingCredentials: 'Awaiting credentials',
    preview: 'Preview', copied: 'Copied', copy: 'Copy', export: 'Export CSV', print: 'Print', search: 'Search',
    save: 'Save', cancel: 'Cancel', savedLocallyDemo: 'Saved locally (demo) — not connected to a backend yet',
    demoData: 'PREVIEW · DEMO DATA',
  },
  status: {
    creator: { active: 'Active', atRisk: 'At Risk', interested: 'Interested', new: 'New', contacted: 'Contacted', application: 'Application', pendingTiktok: 'Pending TikTok', joined: 'Joined', onboarding: 'Onboarding', inactive: 'Inactive', lost: 'Lost', rejected: 'Rejected' },
    partnership: { active: 'Active', pending: 'Pending Signature', expired: 'Expired', terminated: 'Terminated' },
    contract: { draft: 'Draft', underReview: 'Under Review', waitingPartner: 'Waiting on Partner', waitingUgarits: 'Waiting on Ugarits', ready: 'Ready to Sign', partiallySigned: 'Partially Signed', fullySigned: 'Fully Signed', active: 'Active', expiringSoon: 'Expiring Soon', expired: 'Expired', terminated: 'Terminated', superseded: 'Superseded' },
    approval: { pending: 'Pending', approved: 'Approved', rejected: 'Rejected', expired: 'Expired', archived: 'Archived' },
    followup: { open: 'Open', done: 'Done' },
    connector: { connected: 'Connected', pending: 'Pending', awaiting: 'Awaiting Approval', error: 'Error', disabled: 'Disabled' },
    agent: { waitingCredentials: 'Waiting for Credentials', idle: 'Idle', running: 'Running', paused: 'Paused', error: 'Error', configuredPreview: 'Configured (Preview)' },
    system: { notConnected: 'Not Connected', operational: 'Operational', degraded: 'Degraded', down: 'Down' },
    ledger: { notDue: 'Not Due', pending: 'Pending', approved: 'Approved', partiallyPaid: 'Partially Paid', paid: 'Paid', overdue: 'Overdue', disputed: 'Disputed' },
    automation: { draft: 'Draft', waitingConnector: 'Waiting Connector', paused: 'Paused', archived: 'Archived' },
  },
  permission: { level1: 'Level 1 — Read/Draft', level2: 'Level 2 — Needs Approval', level3: 'Level 3 — Owner Only' },
  topbar: {
    openMenu: 'Open menu',
    searchPlaceholder: 'Search anything or ask Ugarits AI...',
    shortcutHint: '⌘K',
    notifications: 'Notifications',
    quickCreate: 'New',
  },
  overview: {
    needsAttention: 'Needs Your Attention', nothingNeedsAttention: 'Nothing needs your attention right now.',
    pendingApprovals: 'Pending Approvals', overdueFollowups: 'Overdue Follow-ups', atRiskCreators: 'At-Risk Creators',
    expiringContracts: 'Expiring Contracts', systemIssues: 'System Issues',
    today: 'Today', todaysFollowups: "Today's Follow-ups", noneToday: 'Nothing due today.',
    recentActivity: 'Recent Activity', noRecentActivity: 'No recent activity.',
    business: 'Business', aiOperations: 'AI Operations',
    agentsConfigured: 'Agents Configured', approvalQueue: 'Approval Queue', connectorIssues: 'Connector Issues',
    aiProvidersLive: 'AI Providers Live', quickActions: 'Quick Actions', reviewApprovals: 'Review Approvals',
  },
  permissionConcept: {
    level0: { label: 'Level 0 — Observe', desc: 'Can read explicitly allowed information only.' },
    level1: { label: 'Level 1 — Advise', desc: 'Can analyze and recommend.' },
    level2: { label: 'Level 2 — Prepare', desc: 'Can prepare external actions but requires human approval.' },
    level3: { label: 'Level 3 — Execute', desc: 'Can execute explicitly pre-authorized low-risk actions. High-risk actions are never autonomous, regardless of level.' },
  },
  agentDept: {
    executive: { name: 'Executive', tagline: 'Coordinates specialists, routes tasks, escalates approvals' },
    operations: { name: 'Operations', tagline: 'Inbox triage, workflow monitoring, daily ops brief' },
    creatorManager: { name: 'Creator Manager', tagline: 'Creator follow-ups, risk analysis, retention' },
    content: { name: 'Content', tagline: 'Research, briefs, scripts, pipeline' },
    sales: { name: 'Sales', tagline: 'Lead qualification, pipeline, proposals' },
    growth: { name: 'Growth', tagline: 'Competitor research, campaign analysis, SEO' },
    finance: { name: 'Finance', tagline: 'Invoice monitoring, receivables, summaries' },
    analytics: { name: 'Analytics', tagline: 'Cross-channel reporting, business insights' },
    developer: { name: 'Developer', tagline: 'Diagnostics, build analysis, deployment recommendations' },
    support: { name: 'Support', tagline: 'System diagnostics, recovery guidance' },
  },
  skills: {
    creatorFollowup: { name: 'Creator Follow-up', desc: 'Drafts a follow-up message for a specific creator.', input: 'Creator ID', output: 'Draft message (requires approval)' },
    creatorRisk: { name: 'Creator Risk Analysis', desc: 'Flags creators at risk from local engagement data.', input: 'Creator roster', output: 'Risk-ranked list' },
    creatorSummary: { name: 'Creator Summary', desc: 'Summarizes a creator\'s status and history.', input: 'Creator ID', output: 'Written summary' },
    inboxTriage: { name: 'Inbox Triage', desc: 'Sorts inbound messages by priority and routes them.', input: 'Connected inbox', output: 'Prioritized queue' },
    competitorResearch: { name: 'Competitor Research', desc: 'Researches a named competitor or market.', input: 'Company/topic', output: 'Research brief' },
    contentResearch: { name: 'Content Research', desc: 'Researches a content topic.', input: 'Topic', output: 'Research notes' },
    contentBrief: { name: 'Content Brief', desc: 'Produces a structured content brief.', input: 'Topic + goal', output: 'Brief document (requires approval)' },
    leadQualification: { name: 'Lead Qualification', desc: 'Scores and qualifies an inbound lead.', input: 'Lead record', output: 'Qualification score + notes' },
    contractSummary: { name: 'Contract Summary', desc: 'Summarizes a contract\'s key terms.', input: 'Contract ID', output: 'Plain-language summary' },
    dailyBrief: { name: 'Daily Executive Brief', desc: 'Summarizes what needs attention today from local data.', input: 'None — reads local repositories', output: 'Prioritized brief' },
    systemDiagnostics: { name: 'System Diagnostics', desc: 'Runs frontend-observable diagnostics.', input: 'None', output: 'Diagnostic report' },
  },
  skillStatus: {
    available_locally: 'Available Locally', requires_connector: 'Requires Connector',
    requires_ai_provider: 'Requires AI Provider', disabled: 'Disabled',
  },
  commandRouter: {
    route: {
      command: 'Ugarits Command', router: 'Local Command Router', unmatched: 'No matching local command',
      approvalService: 'Approval Data Service', creatorService: 'Creator Data Service',
      followupService: 'Follow-up Data Service', contractService: 'Contract Data Service',
      activityService: 'Activity Data Service', connectorService: 'Connector Data Service', systemService: 'System Data Service',
    },
    commands: {
      pendingApprovals: { title: 'Pending Approvals', desc: 'Lists approvals currently awaiting a decision.' },
      creatorsAtRisk: { title: 'Creators at Risk', desc: 'Lists creators flagged high risk.' },
      overdueFollowups: { title: 'Overdue Follow-ups', desc: 'Lists open follow-ups past their due date.' },
      expiringContracts: { title: 'Expiring Contracts', desc: 'Lists contracts expiring within the configured warning window.' },
      recentActivity: { title: 'Recent Activity', desc: 'Shows the most recent activity log entries.' },
      connectorProblems: { title: 'Connector Problems', desc: 'Lists connectors reporting an error or warning state.' },
      systemStatus: { title: 'System Status', desc: 'Shows frontend/backend service status.' },
    },
    empty: {
      noPendingApprovals: 'No pending approvals.', noAtRiskCreators: 'No creators currently at risk.',
      noOverdueFollowups: 'No overdue follow-ups.', noExpiringContracts: 'No contracts expiring soon.',
      noActivity: 'No recent activity.', noConnectorProblems: 'No connector problems.', noSystemServices: 'No system services reporting.',
    },
  },
  aiCommand: {
    title: 'AI Command', subtitle: 'The primary human-to-Ugarits intelligence interface.',
    placeholder: 'Type a command, e.g. "Show pending approvals"...', run: 'Run',
    suggested: 'Suggested commands', recent: 'Recent commands', noRecent: 'No commands run yet this session.',
    routing: 'Routing', localResult: 'LOCAL PREVIEW RESULT', viewAll: 'Open full view',
    notConnectedTitle: 'AI Provider Not Connected',
    notConnectedBody: 'This request needs live reasoning, which requires a secure backend AI provider integration that isn\'t connected yet. Deterministic questions answerable from local data (like the suggestions below) work today.',
    suggestions: {
      attention: 'What needs my attention today?', atRisk: 'Show creators at risk', pendingApprovals: 'Show pending approvals',
      overdueFollowups: 'Show overdue follow-ups', expiringContracts: 'Show expiring contracts',
      summarizeActivity: 'Summarize agency activity', connectorProblems: 'Show connector problems',
      prepareFollowups: 'Prepare creator follow-ups', runDiagnostics: 'Run system diagnostics',
    },
  },
  aiOperations: {
    title: 'AI Operations', subtitle: 'The Ugarits Orchestrator and its specialist agents.',
    orchestrator: 'Ugarits Orchestrator', orchestratorDesc: 'Routes work between specialist agents, providers, and the approval engine. Owns orchestration — providers are replaceable engines underneath it.',
    specialistAgents: 'Specialist Agents', notConfigured: 'Not yet configured',
    status: 'Status', provider: 'Provider', model: 'Model', permission: 'Permission', currentTask: 'Current Task',
    queue: 'Queue', lastActivity: 'Last Activity', tools: 'Tools', memoryScope: 'Memory Scope', approvalRequirement: 'Approval Requirement',
    lastError: 'Last Error', none: 'None', empty: '—', noError: 'No errors recorded',
    opStatus: {
      configured_preview: 'Configured Preview', idle: 'Idle', active: 'Active', working: 'Working',
      waiting_approval: 'Waiting Approval', waiting_connector: 'Waiting Connector', paused: 'Paused', error: 'Error',
    },
    recentPreviewActivity: 'Recent Preview Activity', viewAgent: 'View in Agents',
  },
  palette: {
    title: 'Command Palette',
    placeholder: 'Type a command or search...',
    noResults: 'No matches',
    goTo: 'Go to',
    new: 'New',
    create: 'Create',
    actions: 'Actions',
    toggleTheme: 'Toggle Theme',
    printPage: 'Print Current Page',
    resetDemoData: 'Reset Demo Data (open Settings)',
    runDiagnostics: 'Run Diagnostics (open Support)',
    hint: 'Navigate with ↑ ↓, select with ↵, close with Esc',
  },
  notifications: {
    title: 'Notifications',
    empty: 'No notifications right now — nothing needs your attention.',
    markAllRead: 'Mark all read',
    clear: 'Clear',
    noRemoteDelivery: 'Local preview only — no email, push, or mobile delivery is connected.',
  },
  quickCreate: {
    title: 'Quick Create',
    creator: 'Creator',
    partnership: 'Partnership',
    contract: 'Contract',
    followup: 'Follow-up',
    agent: 'AI Agent',
    automation: 'Automation',
    memory: 'Memory Entry',
    content: 'Content',
    lead: 'Lead',
    client: 'Client',
    project: 'Project',
    approvalNote: 'Approval Note',
    fullName: 'Full name',
    tiktok: 'TikTok username',
  },
  recentlyViewed: { title: 'Recently Viewed', empty: 'Nothing viewed yet' },
  breadcrumbs: { home: 'Overview' },
  detail: {
    creatorTimeline: 'Timeline', creatorPerformance: 'Performance History', nextAction: 'Next Action',
    nextFollowup: 'Next Follow-up', riskReason: 'Risk Reason', auditHistory: 'Audit History',
    noTimelineYet: 'No timeline events yet — activity will appear here once this creator has follow-ups or automations logged.',
    manualActivity: 'Manual note',
    addNotePlaceholder: 'Add a note to this creator\'s timeline...',
  },
  partnershipDetail: {
    tabs: { overview: 'Overview', contacts: 'Contacts', contract: 'Contract', profitSharing: 'Profit Sharing', ledger: 'Payments', documents: 'Documents', activity: 'Activity' },
    documentsEmpty: 'No documents uploaded — signed contract storage requires a backend object store (S3/MinIO), not yet provisioned.',
    ledgerEmpty: 'No ledger entries yet — profit-sharing periods are never fabricated; they post only once real revenue data is connected.',
    addContact: 'Add Contact',
  },
  env: { development: 'DEV', preview: 'PREVIEW', production: 'PRODUCTION' },
  system: {
    apiBackend: 'Backend API', notConnectedDetail: 'No backend deployed yet — dataService.ts still serves mock data.',
    version: 'Version', build: 'Build', environment: 'Environment',
  },
  rowMenu: {
    view: 'View', edit: 'Edit', openFull: 'Open full record', archive: 'Archive', deleteDemo: 'Delete demo record',
  },
  toast: {
    creatorAdded: 'Creator added', creatorUpdated: 'Creator updated', creatorArchived: 'Creator archived',
    demoRecordDeleted: 'Demo record deleted', activityAdded: 'Activity logged',
    followupScheduled: 'Follow-up scheduled', followupCompleted: 'Follow-up marked done',
    approvalApproved: 'Approved', approvalRejected: 'Rejected',
    partnershipCreated: 'Partnership created', partnershipArchived: 'Partnership archived',
    contactAdded: 'Contact added',
    contractCreated: 'Contract created', contractUpdated: 'Contract updated',
    versionDuplicated: 'New version created', contractArchived: 'Contract archived',
    agentAdded: 'AI agent added', agentUpdated: 'AI agent updated', agentArchived: 'AI agent archived',
    calculationSaved: 'Calculation saved to ledger (preview)', settingsSaved: 'Settings saved',
    demoReset: 'Demo data reset to defaults', demoCleared: 'Demo data cleared',
    automationAdded: 'Automation added', automationUpdated: 'Automation updated',
    automationArchived: 'Automation archived', automationDuplicated: 'Automation duplicated',
    memoryAdded: 'Memory entry added', memoryUpdated: 'Memory entry updated', memoryArchived: 'Memory entry archived',
  },
  confirm: {
    archiveCreatorTitle: 'Archive this creator?', archiveCreatorDesc: 'The creator moves to an archived state. You can find them again by filtering; this does not permanently delete anything.',
    deleteCreatorTitle: 'Delete this demo creator?', deleteCreatorDesc: 'This permanently removes this demo record from local preview data. This cannot be undone.',
    deleteFollowupTitle: 'Delete this follow-up?', deleteFollowupDesc: 'This permanently removes this demo follow-up. This cannot be undone.',
    deleteApprovalTitle: 'Delete this demo approval?', deleteApprovalDesc: 'This permanently removes this demo approval record. This cannot be undone.',
    archivePartnershipTitle: 'Archive this partnership?', archivePartnershipDesc: 'The partnership moves to an archived state and stops appearing in active views.',
    archiveContractTitle: 'Archive this contract?', archiveContractDesc: 'The contract moves to an archived state and stops appearing in active views.',
    deleteAgentTitle: 'Delete this demo agent?', deleteAgentDesc: 'This permanently removes this preview-created agent. This cannot be undone.',
    resetDemoTitle: 'Reset all demo data?', resetDemoDesc: 'This replaces everything with the original seed data. Any records you created or edited will be lost.',
    clearDemoTitle: 'Clear all demo data?', clearDemoDesc: 'This empties every local record (creators, follow-ups, approvals, partnerships, contracts, activity). The AI Agent catalog and Connectors stay, since those are configuration, not data. This cannot be undone.',
    deleteAutomationTitle: 'Delete this demo automation?', deleteAutomationDesc: 'This permanently removes this preview-created automation. This cannot be undone.',
    deleteMemoryTitle: 'Delete this demo memory entry?', deleteMemoryDesc: 'This permanently removes this preview-created memory entry. This cannot be undone.',
  },
  empty: {
    noCreators: 'No creators match this view yet.',
    noFollowups: 'Nothing here — you\'re caught up.',
    noApprovals: 'No approvals in this filter.',
    noPartnerships: 'No partnerships yet.',
    noContracts: 'No contracts yet.',
    noContacts: 'No contacts added yet.',
  },
  creatorForm: { editTitle: 'Edit Creator', createTitle: 'New Creator' },
  creatorViews: { all: 'All', active: 'Active', need_followup: 'Needs Follow-up', at_risk: 'At Risk', pending_tiktok: 'Pending TikTok', allRisk: 'All risk levels' },
  followupForm: {
    defaultTitle: 'Follow-up', schedule: 'Schedule Follow-up', creator: 'Creator', title: 'Title',
    due: 'Due', markDone: 'Mark Done', reopen: 'Reopen',
  },
  followupViews: { today: 'Today', overdue: 'Overdue', upcoming: 'Upcoming', completed: 'Completed', all: 'All' },
  approvalFilters: { pending: 'Pending', approved: 'Approved', rejected: 'Rejected', archived: 'Archived', all: 'All' },
  approvalDetail: { show: 'Show payload', hide: 'Hide payload', approve: 'Approve', reject: 'Reject' },
  partnershipForm: {
    newPartnership: 'New Partnership', editTitle: 'Edit Partnership', createTitle: 'New Partnership',
    partnerName: 'Partner name', legalName: 'Legal name', type: 'Type', industry: 'Industry', website: 'Website',
  },
  profitSharing: {
    previewCalculation: 'PREVIEW CALCULATION',
    disclaimer: 'This is a local preview calculation only — not accounting or legal advice. Verify all figures before acting on them.',
    method: 'Method', period: 'Period', revenue: 'Revenue', eligibleRevenue: 'Eligible Revenue',
    partnerPct: 'Partner %', fixedAmount: 'Fixed Amount', adjustments: 'Adjustments',
    partnerShare: 'Partner Share', ugaritsShare: 'Ugarits Share', balance: 'Balance', saveDemo: 'Save Demo Calculation',
  },
  documents: {
    previewOnly: 'Preview only — filename shown locally, not securely stored on a server yet.',
    selected: 'Selected', remove: 'Remove',
  },
  contractForm: {
    newContract: 'New Contract', editTitle: 'Edit Contract', partnership: 'Partnership', partnershipRequired: 'Select a partnership',
    title: 'Title', titleRequired: 'Title is required', type: 'Type', version: 'Version', effectiveDate: 'Effective Date',
    expiryDate: 'Expiry Date', currency: 'Currency', value: 'Value', duplicateVersion: 'Duplicate as New Version',
  },
  agentForm: {
    newAgent: 'New AI Agent', editTitle: 'Edit AI Agent', name: 'Name', role: 'Role', department: 'Department', provider: 'Provider', model: 'Model',
    permissionLevel: 'Permission Level', description: 'Description', allowedActions: 'Allowed Actions',
    forbiddenActions: 'Forbidden Actions', required: 'Required', pause: 'Pause', resume: 'Resume',
  },
  connectors: {
    honestyNote: 'None of these are live connections — enabling one here only turns on its UI preview state. Real data sync requires the connector\'s backend integration.',
    enabled: 'Enabled', disabled: 'Disabled', viewDetails: 'View Details', enable: 'Enable', disable: 'Disable',
    status: 'Status', editLabel: 'Label', notes: 'Notes', requiresLiveIntegration: 'Requires live integration to connect',
  },
  settingsSections: {
    general: 'General', branding: 'Branding', appearance: 'Appearance', language: 'Language', notifications: 'Notification Preferences',
    creatorRules: 'Creators', followupRules: 'Follow-ups', approvalRules: 'Approvals',
    partnershipRules: 'Partnerships', contractRules: 'Contracts', agentRules: 'Agents', automationRules: 'Automations',
    export: 'Export Settings', aiProviders: 'AI Providers', security: 'Security', data: 'Data',
    connectors: 'Connectors', developer: 'Developer', support: 'Support',
  },
  settingsFields: {
    companyName: 'Company name', legalName: 'Legal name', timezone: 'Timezone', defaultCurrency: 'Default currency',
    defaultLandingRoute: 'Default landing page', environment: 'Environment', theme: 'Theme', density: 'Density',
    sidebarCollapsed: 'Collapse sidebar by default', reducedMotion: 'Reduced motion', appLanguage: 'App language',
    dateFormat: 'Date format', numberFormat: 'Number format',
    appName: 'App name', brandColor: 'Brand color', logoStatus: 'Logo', noLogo: 'No logo uploaded yet',
    logoNote: 'Uploading a real logo file requires the backend asset store — not faked here.',
    notif: {
      followupDue: 'Follow-up due', approvalWaiting: 'Approval waiting', creatorAtRisk: 'Creator at risk',
      contractExpiring: 'Contract expiring', systemAlerts: 'System alerts',
      disclaimer: 'These control which Notification Center items appear locally — no email, push, or SMS is sent.',
    },
    defaultFollowupDays: 'Default follow-up days', riskThresholdDays: 'Risk threshold (days)', defaultOwner: 'Default owner',
    idPrefix: 'ID prefix',
    defaultPriority: 'Default priority', overdueBehavior: 'Overdue behavior', overdueBehaviorFlag: 'Flag only',
    overdueBehaviorEscalate: 'Flag + escalate', reminderEnabled: 'Reminder before due date',
    defaultPermissionLevel: 'Default permission level', expirationDays: 'Expires after (days)', highRiskConfirm: 'Require confirmation for high-risk approvals',
    renewalWarningDays: 'Renewal warning (days before)', expiryWarningDays: 'Expiry warning (days before)', defaultNoticePeriodDays: 'Default notice period (days)',
    agentDefaultsNote: 'Applied as the default when creating a new agent. Real enforcement of permission levels happens in the Approvals engine once agents run against a live provider.',
    defaultFileType: 'Default export file type', pdfUnavailable: 'PDF (unavailable in this build)',
    includeBranding: 'Include Ugarits branding on export', includeTimestamp: 'Include timestamp on export',
    fileNamePrefix: 'Exported file name prefix',
    aiKeyNote: 'API keys are never stored in the browser. Connecting a real provider requires a backend to hold credentials securely.',
    dataMode: 'Data mode', demoDataExplanation: 'This app runs on local preview data stored in your browser. Reset restores the original seed data; Clear empties every record.',
    resetDemoData: 'Reset Demo Data', clearAllDemoData: 'Clear All Demo Data',
    connectorsSummary: 'connectors configured', viewConnectors: 'Open Connectors',
    appVersion: 'App version', buildInfo: 'This is a preview build — no CI/CD pipeline metadata is available in this environment.',
    supportNotBuilt: 'Support & Diagnostics is not built yet — nothing here is faked. This section will list diagnostic/AI-support/log-sharing preferences once that module ships.',
    defaultApprovalLevel: 'Default automation approval level',
    automationDefaultsNote: 'Applied as the default when creating a new automation — see Automations.',
    diagnosticsReminder: 'Remind me to run diagnostics periodically',
    diagnosticsReminderNote: 'A local preference only — no scheduled background job exists yet to actually send a reminder.',
    agentLogRetentionNote: 'Agent Log entries persist in this browser\'s local preview storage only; there is no server-side retention policy yet.',
    openSupport: 'Open Support & Diagnostics',
  },
  askAi: {
    title: 'Ask Ugarits AI', notConnected: 'No AI provider is connected — this only runs a small set of safe local lookups against your preview data.',
    placeholder: 'Ask about at-risk creators, pending approvals, expiring contracts...', run: 'Run',
    localResult: 'LOCAL PREVIEW RESULT',
    examples: 'Try one of these',
    example: {
      whatNeedsAttention: 'What needs my attention?',
      showAtRisk: 'Show at-risk creators',
      showPendingApprovals: 'Show pending approvals',
      showExpiringContracts: 'Show expiring contracts',
    },
    shortLabel: 'Ask AI',
  },
  footer: { brand: 'Ugarits Command Center', poweredBy: 'Powered by Ugarits Marketing FZ-LLC', developerCredit: 'Developed by Maher Moustafa' },
  comingSoon: { explanation: 'This module is planned but not built yet. Nothing here is faked or connected — it is a real preview of what\'s coming.', planned: 'Planned capabilities' },
  diagnostics: {
    requiresBackendCheck: 'Requires Backend Check',
    labels: {
      frontendLoad: 'Frontend load status', currentRoute: 'Current route', appVersion: 'App version',
      environment: 'Environment', dataMode: 'Data mode', browser: 'Browser', language: 'Language', theme: 'Theme',
      system: 'System', connectorsTotal: 'Configured connectors', connectorsWithProblem: 'Connectors with a problem',
      configuredAgents: 'Configured agents', aiProvidersConnected: 'AI providers connected',
    },
    values: { frontendLoadOk: 'OK — this page rendered' },
  },
  support: {
    subtitle: 'Diagnostics, error visibility, and safe local recovery guidance — nothing here is faked.',
    systemHealth: 'System Health', diagnostics: 'Diagnostics', runDiagnostics: 'Run Diagnostics', running: 'Running…',
    issuesFound: 'Issues found',
    col: { service: 'Service', status: 'Status', detail: 'Detail', version: 'Version' },
    recentErrors: 'Recent Errors', noErrors: 'No errors recorded.',
    connectorProblems: 'Connector Problems', noConnectorProblems: 'No connector problems.',
    aiProviderProblems: 'AI Provider Problems', noAiProvidersConnected: 'No AI providers are connected yet.',
    appInfo: 'Application Info',
    supportAssistant: 'Support Assistant',
    assistantPlaceholder: 'Describe the problem you\'re seeing...',
    assistantRun: 'Sanitize & Review', assistantSanitized: 'Sanitized text (what would be shared)',
    assistantRedactedCount: 'Redacted items', assistantNoRedactions: 'No credential-like patterns were found.',
    assistantHonestNote: 'AI technical analysis requires secure provider integration — this text is never sent anywhere.',
    recoveryGuidance: 'Recovery Guidance',
    recoverySteps: {
      reload: 'Reload the page — most rendering glitches clear on a fresh load.',
      checkConnectors: 'Check the Connectors page for a connector reporting an error or warning state.',
      checkSystem: 'Check the System page for backend/database connectivity status.',
      resetDemo: 'If local preview data looks corrupted, use Settings → Reset Demo Data (this discards local edits).',
    },
  },
  aiModels: {
    subtitle: 'The routing table between departments and the models they would use once a provider is connected.',
    provider: 'Provider', model: 'Model', purposeCol: 'Purpose', status: 'Status', configured: 'Configured',
    note: 'Connecting a real provider requires backend credential storage — no API key is ever entered or stored in this browser.',
    purpose: {
      executive: 'Executive reasoning & prioritization', creator_manager: 'Creator follow-ups & retention',
      content_director: 'Content pipeline', social_analytics: 'Cross-channel analytics',
      sales: 'Partnership & lead outreach', developer: 'System diagnostics & automation maintenance',
    },
  },
  automations: {
    subtitle: 'Local Preview automation definitions. No execution backend or scheduler exists yet — status is never "Active".',
    empty: 'No automations yet.', duplicate: 'Duplicate', unassigned: 'Unassigned',
    col: { status: 'Status', lastRun: 'Last Run', nextRun: 'Next Run' },
  },
  automationForm: {
    newTitle: 'New Automation', editTitle: 'Edit Automation', name: 'Name', description: 'Description',
    trigger: 'Trigger', conditions: 'Conditions', actions: 'Actions', ownerAgent: 'Owner Agent',
    approvalLevel: 'Approval Level', required: 'Name, trigger, and actions are required',
  },
  memory: {
    badge: 'UGARITS BRAIN — LOCAL PREVIEW MEMORY',
    subtitle: 'Shared local knowledge base for agents and departments. No vector store or embeddings exist yet.',
    searchPlaceholder: 'Search title, content, or tags...', allDomains: 'All domains',
    plainTextSearchNote: 'Plain text search only — not semantic/AI search.',
    empty: 'No memory entries match this filter.', agentAccessCount: 'Agents with access',
  },
  memoryDomain: {
    company: 'Company', brand: 'Brand', services: 'Services', sop: 'SOPs', creators: 'Creators', clients: 'Clients',
    leads: 'Leads', partnerships: 'Partnerships', contracts: 'Contracts', projects: 'Projects', content: 'Content',
    decisions: 'Decisions', knowledge: 'Knowledge', agent_learnings: 'Agent Learnings',
  },
  memoryForm: {
    newTitle: 'New Memory Entry', editTitle: 'Edit Memory Entry', title: 'Title', domain: 'Domain', content: 'Content',
    source: 'Source', tags: 'Tags', tagsPlaceholder: 'Comma-separated tags', visibility: 'Visibility',
    visibilityTeam: 'Team', visibilityPrivate: 'Private', agentAccess: 'Agent Access',
    required: 'Title and content are required',
  },
  agentLog: {
    subtitle: 'A read-only view of every local Preview activity event — agent, user, and system actions alike.',
    actor: { all: 'All actors', agent: 'Agent', user: 'User', system: 'System' },
    result: { all: 'All results', good: 'Good', warn: 'Warning', crit: 'Critical', info: 'Info' },
    col: { when: 'When', actorType: 'Actor Type', actor: 'Actor', action: 'Action', target: 'Target', result: 'Result', usage: 'Token Usage' },
    usageUnavailable: 'Not Available Until Provider Integration',
    empty: 'No activity matches this filter.',
  },
};
export type TranslationShape = typeof en;
